import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useEntitlement, useProfile, useUpdateProfile } from '../../hooks/useApi';
import { trackEvent } from '../../services/analytics';
import { FlashtoolsEntitlement } from '../../types';
import { V2_BILLING_BYPASS_PATHS } from '../nav';
import { useSeatSession } from '../useSeatSession';
import { useV2Nav } from '../useV2Nav';
import { buildTourSteps, TourStep } from './tourSteps';
import { shouldAutoStartTour, writeTourOutcome } from './tourStorage';

/**
 * Tour state for the whole shell.
 *
 * The provider owns which step is current and how the tour moves through the
 * app; the overlay only draws. It sits inside `AppShell`, so every screen
 * can call `useTour().start('manual')` for a replay button, and the shell
 * itself can lend the tour its mobile drawer.
 *
 * Progress is tracked by step id. The step list is derived from the live nav
 * model and can reflow mid-tour (entitlement finishes loading, a banner
 * changes the attention count), and an index into a list that just changed
 * would land on the wrong tab.
 */

export type TourTrigger = 'auto' | 'manual';

export interface DrawerControls {
  open: () => void;
  close: () => void;
}

export interface TourContextValue {
  active: boolean;
  step: TourStep | null;
  /** 0-based position of `step` in `steps`, or -1 when inactive. */
  index: number;
  steps: TourStep[];
  start: (trigger: TourTrigger) => void;
  next: () => void;
  back: () => void;
  /** Ends the tour early and remembers it as skipped. */
  skip: () => void;
  /** Ends the tour from the final step and remembers it as completed. */
  finish: (ctaRoute?: string) => void;
  /**
   * The overlay could not find the current step's anchor and the step is
   * optional: move past it in whichever direction the user was going.
   */
  skipMissingAnchor: () => void;
  /** Shell plumbing: the mobile drawer the tour may open to reach nav items. */
  registerDrawer: (controls: DrawerControls | null) => void;
  drawer: DrawerControls | null;
}

const noop = () => undefined;

/**
 * A no-op default, rather than a throw, so a screen that renders a "Take the
 * tour" button still works in a test that mounts it without the shell.
 */
const INACTIVE: TourContextValue = {
  active: false,
  step: null,
  index: -1,
  steps: [],
  start: noop,
  next: noop,
  back: noop,
  skip: noop,
  finish: noop,
  skipMissingAnchor: noop,
  registerDrawer: noop,
  drawer: null,
};

const TourContext = createContext<TourContextValue>(INACTIVE);

export function useTour(): TourContextValue {
  return useContext(TourContext);
}

/** Same predicate NewUiApp uses for the billing redirect; kept local to avoid a cycle. */
function hasAccess(ent: FlashtoolsEntitlement | undefined): boolean {
  if (!ent) return false;
  return Boolean(ent.comped || ent.tier);
}

/** How long after the shell settles before the welcome card appears. */
const AUTO_START_DELAY_MS = 900;

export const TourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading: authLoading, isAdminSession } = useAuth();
  const { data: entitlement, isLoading: entitlementLoading } = useEntitlement();
  const { data: profile, isLoading: profileLoading } = useProfile();
  const updateProfile = useUpdateProfile();
  const { isLoading: seatLoading } = useSeatSession();
  const { sections } = useV2Nav();
  const location = useLocation();
  const navigate = useNavigate();

  const [stepId, setStepId] = useState<string | null>(null);
  const [drawer, setDrawer] = useState<DrawerControls | null>(null);
  const autoStartAttempted = useRef(false);

  const steps = useMemo(
    () =>
      buildTourSteps({
        sections,
        hasAccess: hasAccess(entitlement),
        bypassPaths: V2_BILLING_BYPASS_PATHS,
      }),
    [sections, entitlement],
  );

  const active = stepId !== null;
  let index = active ? steps.findIndex((s) => s.id === stepId) : -1;
  // The list reflowed under us and the current step vanished (a tab was
  // hidden mid-tour, or the entitlement query blinked). Stay at the same
  // position in the new list rather than bounce the user back to the welcome
  // card; the effects below then re-anchor whatever step now sits there.
  const lastIndex = useRef(0);
  if (active && index === -1) index = Math.min(lastIndex.current, steps.length - 1);
  if (active) lastIndex.current = index;
  const step = active ? steps[index] ?? null : null;

  const uid = user?.uid ?? null;

  const end = useCallback(
    (outcome: 'completed' | 'skipped') => {
      if (uid) writeTourOutcome(uid, outcome);
      // The account remembers too, so another browser does not replay it.
      // Best effort: a failed write leaves the localStorage copy in charge.
      if (!isAdminSession && profile?.flashtools_onboarding_tour !== outcome) {
        updateProfile.mutate({ flashtools_onboarding_tour: outcome }, { onError: () => undefined });
      }
      setStepId(null);
      drawer?.close();
    },
    [uid, drawer, isAdminSession, profile?.flashtools_onboarding_tour, updateProfile],
  );

  const start = useCallback(
    (trigger: TourTrigger) => {
      const first = steps[0];
      if (!first) return;
      setStepId(first.id);
      trackEvent('onboarding_tour_started', { trigger, steps: steps.length });
    },
    [steps],
  );

  // Which way the last move went, so an optional step with no anchor is
  // skipped onward when reached by Next and backward when reached by Back.
  const direction = useRef<1 | -1>(1);

  const goTo = useCallback(
    (target: TourStep | undefined) => {
      if (!target) return;
      setStepId(target.id);
    },
    [],
  );

  const next = useCallback(() => {
    if (!step) return;
    if (step.kind === 'done') {
      end('completed');
      return;
    }
    // "Next" on a click-here step does the click for the user.
    if (step.kind === 'nav' && step.interactive && step.route) {
      navigate(step.route);
    }
    direction.current = 1;
    goTo(steps[index + 1]);
  }, [step, steps, index, navigate, goTo, end]);

  const back = useCallback(() => {
    if (!step || index <= 0) return;
    direction.current = -1;
    goTo(steps[index - 1]);
  }, [step, steps, index, goTo]);

  const skipMissingAnchor = useCallback(() => {
    if (!step || !step.optional) return;
    const target = steps[index + direction.current] ?? steps[index + 1];
    goTo(target);
  }, [step, steps, index, goTo]);

  const skip = useCallback(() => {
    if (!step) return;
    trackEvent('onboarding_tour_skipped', { step_id: step.id, index });
    end('skipped');
  }, [step, index, end]);

  const finish = useCallback(
    (ctaRoute?: string) => {
      trackEvent('onboarding_tour_completed', { steps: steps.length });
      end('completed');
      if (ctaRoute) navigate(ctaRoute);
    },
    [steps.length, end, navigate],
  );

  const registerDrawer = useCallback((controls: DrawerControls | null) => {
    setDrawer(controls);
  }, []);

  // Each step is reported once, when it becomes current.
  useEffect(() => {
    if (!step) return;
    trackEvent('onboarding_tour_step_viewed', { step_id: step.id, index, steps: steps.length });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step?.id]);

  // A page step must be looked at on its own page. Landing here via "Back",
  // or via a nav step whose click the user made elsewhere, navigates first.
  useEffect(() => {
    if (!step || step.kind !== 'page' || !step.route) return;
    if (!location.pathname.startsWith(step.route)) navigate(step.route);
  }, [step, location.pathname, navigate]);

  // Where the app was when the current step appeared. A nav step whose tab is
  // already open (the tour started on Billing, say) must still show its
  // "click here" card rather than skip straight to the page step.
  const stepStartPath = useRef(location.pathname);
  useEffect(() => {
    stepStartPath.current = location.pathname;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step?.id]);

  // The user clicked the highlighted tab: the route changed to where the nav
  // step points, so move on to that tab's page step.
  useEffect(() => {
    if (!step || step.kind !== 'nav' || !step.interactive || !step.route) return;
    if (location.pathname === stepStartPath.current) return;
    if (location.pathname.startsWith(step.route)) {
      direction.current = 1;
      goTo(steps[index + 1]);
    }
  }, [location.pathname, step, steps, index, goTo]);

  // First sign-in after sign-up: start once the shell knows who this is and
  // whether the billing redirect has already fired.
  //
  // The timer is held in a ref and cleared only on unmount, never by the
  // effect's own cleanup: the entitlement query refetches as screens mount,
  // its loading flag flips for a moment each time, and a cleanup tied to that
  // flag would cancel the timer it had just armed. Unmount also resets the
  // attempt so StrictMode's simulated remount arms it again.
  const startRef = useRef(start);
  startRef.current = start;
  const latest = useRef({
    uid,
    user,
    isAdminSession,
    pathname: location.pathname,
    serverOutcome: profile?.flashtools_onboarding_tour,
  });
  latest.current = {
    uid,
    user,
    isAdminSession,
    pathname: location.pathname,
    serverOutcome: profile?.flashtools_onboarding_tour,
  };
  const autoStartTimer = useRef<number | null>(null);
  useEffect(() => {
    if (autoStartAttempted.current) return;
    if (authLoading || entitlementLoading || seatLoading || profileLoading) return;
    autoStartAttempted.current = true;
    const snapshot = latest.current;
    const eligible = shouldAutoStartTour({
      uid: snapshot.uid,
      creationTime: snapshot.user?.metadata?.creationTime,
      isAdminSession: snapshot.isAdminSession,
      pathname: snapshot.pathname,
      serverOutcome: snapshot.serverOutcome,
    });
    if (!eligible) return;
    autoStartTimer.current = window.setTimeout(() => {
      autoStartTimer.current = null;
      startRef.current('auto');
    }, AUTO_START_DELAY_MS);
  }, [authLoading, entitlementLoading, seatLoading, profileLoading]);
  useEffect(
    () => () => {
      if (autoStartTimer.current !== null) window.clearTimeout(autoStartTimer.current);
      autoStartTimer.current = null;
      autoStartAttempted.current = false;
    },
    [],
  );

  const value = useMemo<TourContextValue>(
    () => ({
      active,
      step,
      index,
      steps,
      start,
      next,
      back,
      skip,
      finish,
      skipMissingAnchor,
      registerDrawer,
      drawer,
    }),
    [active, step, index, steps, start, next, back, skip, finish, skipMissingAnchor, registerDrawer, drawer],
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
};
